/**
 * DeploymentsTab — Read-only deployment history (ADR-100 §3 deploy audit).
 *
 * Each row shows: mode · scope · timestamp · actor · correlationId · reason.
 * The reason field is the primary auditability artifact — ECTP Rule 7.
 */
import { ClockCounterClockwise, Rocket } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { Card, CardBody, CardHeader } from '../../../shared/ui/Card';
import { StatusBadge, type StatusVariant } from '../../../shared/ui/StatusBadge';
import { cn } from '../../../shared/utils/cn';
import { useProfileDeployments } from '../../../features/business-profiles/useBusinessProfiles';
import type {
  DeploymentMode,
  DeploymentRecord,
} from '../../../features/business-profiles/types';

const MODE_VARIANT: Record<DeploymentMode, StatusVariant> = {
  shadow: 'info',
  canary: 'warning',
  full: 'success',
  rollback: 'danger',
};

export function DeploymentsTab({
  subVerticalKey,
}: {
  subVerticalKey: string;
}): JSX.Element {
  const { t, i18n } = useTranslation('adminProfiles');
  const deploymentsQuery = useProfileDeployments(subVerticalKey);

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleString(i18n.language, {
        dateStyle: 'medium',
        timeStyle: 'short',
      });
    } catch {
      return iso;
    }
  };

  const formatScope = (record: DeploymentRecord): string => {
    switch (record.tenantScope) {
      case 'all':
        return t('deployments.scopeAll');
      case 'percentage':
        return t('deployments.scopePercentage', {
          value: record.percentage ?? 0,
        });
      case 'tenant_list':
        return t('deployments.scopeTenantList', {
          count: record.tenantIds?.length ?? 0,
        });
    }
  };

  return (
    <Card>
      <CardHeader>
        <div>
          <h2 className="text-biz-lg biz-heading font-semibold">
            {t('deployments.title')}
          </h2>
          <p className="text-biz-sm text-biz-muted-foreground mt-biz-content">
            {t('deployments.hint')}
          </p>
        </div>
      </CardHeader>
      <CardBody>
        {deploymentsQuery.isLoading ? (
          <LoadingSkeleton />
        ) : deploymentsQuery.isError ? (
          <div
            role="alert"
            className="px-biz-content-lg py-biz-content rounded-biz-btn bg-biz-danger-subtle text-biz-danger border border-biz-danger/30 text-biz-sm"
          >
            {t('deployments.loadError')}
          </div>
        ) : (deploymentsQuery.data?.length ?? 0) === 0 ? (
          <p className="text-biz-sm text-biz-muted-foreground text-center py-biz-section">
            {t('deployments.empty')}
          </p>
        ) : (
          <ol className="relative space-y-biz-content-lg">
            {deploymentsQuery.data!.map((d) => (
              <li
                key={d.id}
                className={cn(
                  'relative pl-12',
                  'before:absolute before:left-4 before:top-2 before:bottom-0 before:w-px before:bg-biz-border',
                  'last:before:hidden',
                )}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    'absolute left-0 top-0 w-8 h-8 rounded-biz-full flex items-center justify-center',
                    'bg-biz-surface border border-biz-border',
                  )}
                >
                  {d.deploymentMode === 'rollback' ? (
                    <ClockCounterClockwise
                      size={16}
                      weight="regular"
                      className="text-biz-danger"
                    />
                  ) : (
                    <Rocket size={16} weight="regular" className="text-biz-primary" />
                  )}
                </span>
                <div className="pb-biz-content-lg">
                  <div className="flex items-center gap-biz-content flex-wrap mb-biz-content">
                    <StatusBadge
                      variant={MODE_VARIANT[d.deploymentMode]}
                      label={t(`deployments.modes.${d.deploymentMode}` as const)}
                    />
                    <span className="font-mono text-biz-xs text-biz-muted-foreground">
                      v{d.profileVersion}
                    </span>
                    <span aria-hidden="true" className="text-biz-muted-foreground">
                      ·
                    </span>
                    <span className="text-biz-xs text-biz-muted-foreground">
                      {formatScope(d)}
                    </span>
                  </div>
                  <p className="text-biz-sm text-biz-foreground">{d.reason}</p>
                  <div className="mt-biz-content text-biz-xs text-biz-muted-foreground flex items-center gap-biz-content flex-wrap">
                    <span>{d.actorName}</span>
                    <span aria-hidden="true">·</span>
                    <time dateTime={d.createdAt}>{formatDate(d.createdAt)}</time>
                    <span aria-hidden="true">·</span>
                    <code className="font-mono">{d.correlationId}</code>
                  </div>
                  {d.rolledBackAt ? (
                    <div
                      role="note"
                      className="mt-biz-content text-biz-xs text-biz-danger inline-flex items-center gap-1.5"
                    >
                      <ClockCounterClockwise size={14} weight="regular" aria-hidden="true" />
                      {t('deployments.rolledBackAt', { at: formatDate(d.rolledBackAt) })}
                    </div>
                  ) : null}
                </div>
              </li>
            ))}
          </ol>
        )}
      </CardBody>
    </Card>
  );
}

function LoadingSkeleton(): JSX.Element {
  return (
    <div className="space-y-biz-content-lg" aria-busy="true" aria-live="polite">
      {[0, 1, 2].map((i) => (
        <div key={i} className="h-20 rounded-biz-card bg-biz-muted animate-pulse" aria-hidden="true" />
      ))}
    </div>
  );
}
